import React, { useEffect, useRef } from 'react';

import { Loader } from '@/components/shared/Loader';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { RoundedButton } from '@/components/shared/RoundedButton';
import {
	WordCloudChart,
	WordCloudChartDataItem,
} from '@/components/shared/WordCloudChart';
import {
	ThematicalAnalysisOutputItemInput,
	TheologicalAnalysisOutputItem,
	useGetBiblePassageAnalysisMutation,
	useGetLyricsAndPassageSummaryMutation,
} from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import BibleVerseEmotions from '../GenerateSetlist/components/BibleVerseEmotions/BibleVerseEmotions';
import styles from './SongAnalysis.module.scss';

export interface SongAnalysisProps {
	bibleReference: string;
	teamSongId: string;
	onDataError?: () => void;
}

export const theologicalOutputToWordCloudChartData = (
	data: TheologicalAnalysisOutputItem[],
): WordCloudChartDataItem[] => {
	return data.map((item) => ({
		label: item.theology,
		value: item.strength || 0,
	}));
};

export const thematicalOutputToWordCloudChartData = (
	data: ThematicalAnalysisOutputItemInput[],
): WordCloudChartDataItem[] => {
	return data.map((item) => ({
		label: item.theme,
		value: item.strength || 0,
	}));
};

const hasEnoughItems = (obj: { left: unknown[]; right: unknown[] }): boolean =>
	Math.max(obj.left.length, obj.right.length) > 6;

export const SongAnalysis: React.FC<SongAnalysisProps> = ({
	bibleReference,
	teamSongId,
	onDataError,
}) => {
	const { handleError } = useErrorHandler();
	const fireViewMoreEvent = useAnalyticsEvent('Analysis_View_More');

	const [showMoreOpened, setShowMoreOpened] = React.useState<{
		summary: boolean;
		theology: boolean;
		themes: boolean;
	}>({
		summary: false,
		theology: false,
		themes: false,
	});

	const { findTeamSongsByIdReduced, isLoading: isTeamSongsLoading } =
		useTeamSongs();

	const {
		data: biblePassageData,
		isLoading: isBibleVerseLoading,
		mutateAsync: getBiblePassageAnalysis,
	} = useGetBiblePassageAnalysisMutation();

	const {
		mutateAsync: getLyricsAndPassageSummary,
		isLoading: isSummariesLoading,
		data: summariesData,
	} = useGetLyricsAndPassageSummaryMutation();

	const teamSongs = findTeamSongsByIdReduced([teamSongId]);
	const teamSong = teamSongs[0];

	useEffect(() => {
		getBiblePassageAnalysis({
			reference: bibleReference,
		});
	}, [bibleReference, getBiblePassageAnalysis]);

	useEffect(() => {
		if (teamSong?.lyrics && teamSong?.name) {
			getLyricsAndPassageSummary({
				input: {
					reference: bibleReference,
					lyrics: teamSong.lyrics,
					title: teamSong.name,
				},
			});
		}
	}, [
		bibleReference,
		teamSong?.lyrics,
		teamSong?.name,
		getLyricsAndPassageSummary,
	]);

	const handleViewMore = (tagCategory: keyof typeof showMoreOpened) => {
		fireViewMoreEvent({
			tagCategory,
		});
		setShowMoreOpened((prev) => ({
			...prev,
			[tagCategory]: !prev[tagCategory],
		}));
	};

	const isLoading =
		isBibleVerseLoading ||
		isSummariesLoading ||
		isTeamSongsLoading ||
		!biblePassageData ||
		!summariesData ||
		!teamSong;

	const dataReady =
		!!teamSong &&
		!!biblePassageData?.getBiblePassageAnalysis &&
		!!summariesData?.getLyricsAndPassageSummary;

	const errorReportedRef = useRef(false);

	useEffect(() => {
		if (isLoading) {
			errorReportedRef.current = false;
			return;
		}
		if (!dataReady && onDataError && !errorReportedRef.current) {
			errorReportedRef.current = true;
			handleError({
				error: new Error('Missing data for song analysis'),
				errorCode: 'SONG_ANALYSIS_MISSING_DATA',
				friendlyMessage:
					'There was an error loading the song analysis data. Please try again later.',
			});
			onDataError();
		}
	}, [isLoading, dataReady, onDataError, handleError]);

	if (!isLoading && !dataReady) {
		return null;
	}

	const theology = {
		left:
			biblePassageData?.getBiblePassageAnalysis?.theologicalKeywords ||
			[],
		right: teamSong!.analysisOutput?.theologicalKeywords || [],
	};
	const themes = {
		left: biblePassageData?.getBiblePassageAnalysis?.thematicKeywords || [],
		right: teamSong!.analysisOutput?.thematicKeywords || [],
	};
	const labels = {
		left: bibleReference,
		right: teamSong!.name,
	};

	return (
		<div className={styles['list']}>
			{isLoading ? (
				<ModalSectionCard paddingSize="md">
					<Loader fullText="Thinking" height={300} />
				</ModalSectionCard>
			) : (
				<>
					<ModalSectionCard
						paddingSize="md"
						className={
							showMoreOpened.summary
								? styles['expanded']
								: styles['closed']
						}
					>
						<ModalSectionTitle
							sideComponent={
								<RoundedButton
									onClick={() => {
										handleViewMore('summary');
									}}
								>
									{showMoreOpened.summary
										? 'Show less'
										: 'Show more'}
								</RoundedButton>
							}
						>
							Analysis Summary
						</ModalSectionTitle>

						<div className={styles['summary']}>
							<p>
								{
									summariesData.getLyricsAndPassageSummary
										?.lyrics
								}
							</p>
							<p>
								{
									summariesData.getLyricsAndPassageSummary
										?.passage
								}
							</p>
							<p>
								{
									summariesData.getLyricsAndPassageSummary
										?.summary
								}
							</p>
						</div>
						<div className={styles['fader']} />
					</ModalSectionCard>

					<ModalSectionCard paddingSize="md">
						<ModalSectionTitle
							sideComponent={
								hasEnoughItems(theology) ? (
									<RoundedButton
										onClick={() => {
											handleViewMore('theology');
										}}
									>
										{showMoreOpened.theology
											? 'Show less'
											: 'Show more'}
									</RoundedButton>
								) : undefined
							}
						>
							Theology
						</ModalSectionTitle>

						<WordCloudChart
							sourceLists={[
								theologicalOutputToWordCloudChartData(
									theology.left,
								),
								theologicalOutputToWordCloudChartData(
									theology.right,
								),
							]}
							reduced={!showMoreOpened.theology}
							labels={[labels.left, labels.right]}
						/>
					</ModalSectionCard>

					<ModalSectionCard paddingSize="md">
						<ModalSectionTitle
							sideComponent={
								hasEnoughItems(themes) ? (
									<RoundedButton
										onClick={() => {
											handleViewMore('themes');
										}}
									>
										{showMoreOpened.themes
											? 'Show less'
											: 'Show more'}
									</RoundedButton>
								) : undefined
							}
						>
							Themes
						</ModalSectionTitle>

						<WordCloudChart
							sourceLists={[
								thematicalOutputToWordCloudChartData(
									themes.left,
								),
								thematicalOutputToWordCloudChartData(
									themes.right,
								),
							]}
							reduced={!showMoreOpened.themes}
							labels={[labels.left, labels.right]}
						/>
					</ModalSectionCard>

					<BibleVerseEmotions
						title="Emotions"
						biblePassageAnalysis={
							biblePassageData?.getBiblePassageAnalysis
						}
						reference={bibleReference}
						songId={teamSongId}
					/>
				</>
			)}
		</div>
	);
};
